import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "jc-builder-state-v1";

const DEFAULT_JOB = {
  parse_id: null,
  preset_id: null,
  price_rule: { mode: "original", type: "percent", value: 0 },
  price_source: "regular",
  show_price: true,
  currency: "$",
  language: "en",
  layout: null,
  show_video_tiles: true,
  show_cert_tiles: true,
  show_cover: true,
  defaults_v: 2,
  concept: null,
  shape_map: {},
  overrides: {
    name: "Catalog",
    tagline: "",
    heading_font: "Fraunces",
    body_font: "Manrope",
    primary_color: "#1A1A1A",
    accent_color: "#CBA135",
    background_color: "#F9F9F8",
    page_size: "A4",
    logo_file_id: null,
  },
};

const BuilderCtx = createContext(null);

function readInitialState() {
  if (typeof window === "undefined") return { parse: null, job: DEFAULT_JOB, lastJob: null, lastMediaJob: null };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { parse: null, job: DEFAULT_JOB, lastJob: null, lastMediaJob: null };
    const s = JSON.parse(raw);
    return {
      parse: s.parse || null,
      job: {
        ...DEFAULT_JOB,
        ...(s.job || {}),
        // Contact Sheet was replaced by the 6-per-page grid
        layout: s.job?.layout === "contact_sheet" ? "grid6" : (s.job?.layout ?? null),
        // v2: certificate tiles became on-by-default — apply once to saved sessions
        ...((s.job?.defaults_v || 1) < 2 ? { show_cert_tiles: true, defaults_v: 2 } : {}),
        overrides: { ...DEFAULT_JOB.overrides, ...(s.job?.overrides || {}) },
      },
      lastJob: s.lastJob || null,
      lastMediaJob: s.lastMediaJob || null,
    };
  } catch (_) {
    return { parse: null, job: DEFAULT_JOB, lastJob: null, lastMediaJob: null };
  }
}

export function BuilderProvider({ children }) {
  const initial = readInitialState();
  const [parse, setParse] = useState(initial.parse);
  const [job, setJob] = useState(initial.job);
  const [lastJob, setLastJob] = useState(initial.lastJob);
  const [lastMediaJob, setLastMediaJob] = useState(initial.lastMediaJob);
  const skipPersistRef = useRef(false);

  // Persist on every change — but skip the render immediately following a reset,
  // otherwise the default state overwrites the removeItem call.
  useEffect(() => {
    if (skipPersistRef.current) {
      skipPersistRef.current = false;
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ parse, job, lastJob, lastMediaJob }));
    } catch (_) {
      /* ignore quota errors */
    }
  }, [parse, job, lastJob, lastMediaJob]);

  // Rehydrate on mount: if a parse_id was persisted but the server no longer
  // has it (e.g. after a full db reset), silently drop the stale parse.
  useEffect(() => {
    const id = parse?.id;
    if (!id) return;
    api.get(`/parse/${id}`)
      .then((r) => {
        // refresh with server truth so items stay in sync with backend
        setParse((prev) => (prev?.id === id ? { ...prev, ...r.data } : prev));
      })
      .catch((e) => {
        if (e?.response?.status === 404) {
          setParse(null);
          setJob((j) => ({ ...j, parse_id: null }));
          setLastJob(null);
          setLastMediaJob(null);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetBuilder = useCallback(() => {
    skipPersistRef.current = true;
    setParse(null);
    setJob(DEFAULT_JOB);
    setLastJob(null);
    setLastMediaJob(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {
      /* ignore storage errors */
    }
  }, []);

  return (
    <BuilderCtx.Provider value={{ parse, setParse, job, setJob, lastJob, setLastJob, lastMediaJob, setLastMediaJob, resetBuilder }}>
      {children}
    </BuilderCtx.Provider>
  );
}

export function useBuilder() {
  const ctx = useContext(BuilderCtx);
  if (!ctx) throw new Error("useBuilder must be used within <BuilderProvider>");
  return ctx;
}
